import { h } from "preact";
import { useState, useMemo, useEffect } from "preact/hooks";

import { Island } from "../../ui/layout/Island.js";
import { OptionList } from "../../ui/forms/OptionList.js";
import { QuantitySelector } from "../../ui/forms/QuantitySelector.js";
import { ColorList } from "../../ui/display/ColorList.js";
import { Markups } from "../../ui/display/Markups.js";
import { PrintColors } from "../../ui/display/PrintColors.js";
import { Results } from "../Results.js";

import { init } from "../../../data/calculators/zip/init.js";
import { totalPrice } from "../../../data/calculators/zip/calc.js";
import {
  densityScale,
  sizeScale,
  defaultSize,
  colorsScale,
  defaultColor,
} from "../../../data/calculators/zip/packs.js";

export function CalculatorZip({ onchange }) {
  const [density, setDensity] = useState(init.density);
  const [size, setSize] = useState(init.size);
  const [color, setColor] = useState(init.color);
  const [faceColors, setFaceColors] = useState(init.face_colors);
  const [rearColors, setRearColors] = useState(init.rear_colors);
  const [samePrint, setSamePrint] = useState(init.same_print);
  const [quantity, setQuantity] = useState(init.quantity);
  const [quantityManual, setQuantityManual] = useState(init.quantity_manual);
  const [markups, setMarkups] = useState(init.markups);

  const state = {
    density,
    size,
    color,
    face_colors: faceColors,
    rear_colors: rearColors,
    same_print: samePrint,
    quantity,
    quantity_manual: quantityManual,
    markups,
  };

  const total = useMemo(() => totalPrice(state), [
    density, size, color,
    faceColors, rearColors, samePrint,
    quantity, quantityManual, markups,
  ]);

  useEffect(() => {
    if (onchange) onchange(state, total);
  }, [density, size, color, faceColors, rearColors, samePrint, quantity, quantityManual, markups]);

  function selectDensity(newDensity) {
    const newSize = defaultSize(newDensity);
    const newColor = defaultColor(newDensity, newSize, color);
    setDensity(newDensity);
    setSize(newSize);
    setColor(newColor.name);
  }

  function selectSize(newSize) {
    const newColor = defaultColor(density, newSize, color);
    setSize(newSize);
    setColor(newColor.name);
  }

  function selectColor(newColor) {
    setColor(newColor);
  }

  function changeColors({ side, value }) {
    setSamePrint(false);
    if (side === "face") setFaceColors(value);
    else setRearColors(value);
  }

  function toggleMarkup(idx) {
    setMarkups((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  }

  return h("div", { class: "calc-calculator" },
    h("div", { class: "calc-calculator__grid" },
      h(Island, { title: "Плотность", classes: "w-full md:w-1/3" },
        h(OptionList, {
          items: densityScale(),
          selected: density,
          template: "*мкм",
          onchange: selectDensity,
        })
      ),

      h(Island, { title: "Размер пакетов", classes: "w-full md:w-2/3" },
        h(OptionList, {
          items: sizeScale(density),
          selected: size,
          template: "*мм",
          onchange: selectSize,
        })
      ),

      h(Island, { title: "Цвета пакетов", classes: "w-full" },
        h(ColorList, {
          colors: colorsScale(density, size),
          selected: color,
          onchange: selectColor,
        })
      ),

      h(Island, { title: "Количество цветов печати", classes: "w-full sm:w-2/3 lg:w-2/5" },
        h(PrintColors, {
          face_colors: faceColors,
          rear_colors: rearColors,
          same_print: samePrint,
          oncolorschange: changeColors,
          onsameprintcahnge: setSamePrint,
        })
      ),

      h(Island, { title: "Тираж", classes: "w-full sm:w-1/3 lg:w-1/5" },
        h(QuantitySelector, {
          quantity,
          quantity_manual: quantityManual,
          min_quantity: init.min_quantity,
          onchange: setQuantity,
          onmanualchange: setQuantityManual,
        })
      ),

      h(Island, { title: "Наценки", classes: "w-full lg:w-2/5" },
        h(Markups, {
          markups,
          ontoggle: toggleMarkup,
        })
      )
    ),

    h(Results, {
      title: "ЗИП пакеты",
      state,
      totalPrice: total,
    })
  );
}
